import { Injectable } from '@angular/core';
import { TranslatePipe } from 'src/app/shared/pipe/translate.pipe';
import { Language } from 'src/app/models/language.model';
import { LabelInfo } from 'src/app/models/label-info.model';
import { LabelKeys } from 'src/app/models/enums/label-keys';
import {
  DimensionType,
  ManualInputControlType,
  SectionType,
  StepControlType,
  FluxType,
  FormModel,
  AppDocument,
  SignatureType,
} from 'src/app/models/forms.model';
import { StepData, SectionData, ControlData } from '../flux.model';
import { v4 as uuidv4 } from 'uuid';
import { TranslationsHelper } from './translations-helper';

@Injectable({
  providedIn: 'root',
})
export class FluxHelper {
  constructor(private translate: TranslatePipe) { }

  /**
   * Generate unique V1-compatible identifier
   */
  generateUniqueId(prefix: string): string {
    // Match FluxV1 pattern: prefix_UUID
    const uuid = uuidv4();
    return prefix ? uuid : uuid;
  }

  /**
   * Create translations only for supported languages
   */
  private createTranslationsIfSupported(
    supportedLanguages: Language[],
    values: { iso: string; value: string }[]
  ): LabelInfo[] {
    let list: LabelInfo[] = [];
    values.forEach((v) => {
      const langId = supportedLanguages.find((lang) => lang.iso === v.iso)?.id;
      if (langId && langId > 0) {
        list.push({
          languageId: langId,
          languageIso: v.iso,
          value: v.value,
        });
      }
    });
    return list;
  }

  /**
   * Create a manual input control
   */
  private createManualInputControl(
    name: string,
    identifier: string,
    type: ManualInputControlType,
    isMandatory: boolean = true,
    dimension: number = 50
  ): ControlData {
    return {
      identifier,
      type: StepControlType.ManualInput,
      title: name,
      canEdit: false,
      canDepend: false,
      isOnlyForConfig: false,
      isPresentationControl: false,
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      isMandatory: false,
      translations: [],
      dimension,
      dimensionType: DimensionType.Percentage,
      autoDimension: false,
      specificControlData: {
        type,
        isMandatory,
        allowedCharacters: '',
        maxCharacters: type === ManualInputControlType.Text ? 50 : undefined,
        minValue: type === ManualInputControlType.Numeric ? 0 : undefined,
        maxValue: type === ManualInputControlType.Numeric ? 9999999 : undefined,
        allowOnlyFuture: false,
        allowOnlyPast: false,
        isSimpleValidation: false,
        isApiValidation: false,
      },
      errorsTranslations: [],
    };
  }

  /**
   * Create a paragraph control
   */
  private createParagraphControl(
    text: string,
    identifier: string,
    showValidations: boolean = false,
    isMandatory: boolean = false
  ): ControlData {
    return {
      identifier,
      type: StepControlType.Paragraph,
      title: text,
      canEdit: false,
      canDepend: false,
      isMandatory: false,
      isPresentationControl: false,
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      translations: [],
      isOnlyForConfig: false,
      dimension: 100,
      dimensionType: DimensionType.Percentage,
      autoDimension: true,
      specificControlData: {
        descriptionTranslations: [],
        showValidations,
        isMandatory,
        isMandatoryTranslations: [],
      },
      errorsTranslations: [],
    };
  }

  /**
   * Create a new row control
   */
  private createNewRowControl(): ControlData {
    return {
      identifier: this.generateUniqueId('newrow'),
      type: StepControlType.NewRow,
      title: 'New Row',
      canEdit: false,
      canDepend: false,
      isOnlyForConfig: false,
      isPresentationControl: false,
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      isMandatory: false,
      translations: [],
      dimension: 100,
      dimensionType: DimensionType.Percentage,
      autoDimension: true,
      specificControlData: {},
      errorsTranslations: [],
    };
  }

  /**
   * Create KYC step with all default sections
   */
  createKycStep(supportedLanguages: Language[]): StepData {
    const stepName = this.translate.transform(LabelKeys.flux.identification);
    const step: StepData = {
      identifier: this.generateUniqueId(`${stepName} - KYC`),
      name: stepName,
      type: FluxType.Kyc,
      sections: [],
      isVisible: true,
      config: {},
      translations:
        TranslationsHelper.createKycTranslations(supportedLanguages),
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    // Section 1: KYC Status
    const statusSection: SectionData = {
      title: this.translate.transform(LabelKeys.flux.stepInfo.kycStatusTitle),
      type: SectionType.Presentation,
      controls: [
        {
          type: StepControlType.CheckBox,
          identifier: 'IdentityValidationStatus',
          title: this.translate.transform(
            LabelKeys.flux.stepInfo.validationStatus
          ),
        } as ControlData,
      ],
      identifier: this.generateUniqueId(
        this.translate.transform(LabelKeys.flux.stepInfo.kycStatusTitle)
      ),
    } as SectionData;

    // Section 2: Document Data (General)
    const documentData = [
      'Surname',
      'GivenNames',
      'SurnameAndGivenNames',
      'PersonalNumber',
      'Sex',
      'Nationality',
      'NationalityCode',
      'DateOfBirth',
      'PlaceOfBirth',
      'Age',
      'AgeAtIssue',
      'DateOfIssue',
      'DateOfExpiry',
      'DocumentSeries',
      'BookletNumber',
      'DocumentNumber',
      'DocumentClassCode',
      'IssuingStateName',
      'IssuingStateCode',
      'Authority',
      'AuthorityCode',
      'MRZStrings',
      'YearsSinceIssue',
    ];
    const documentControls: ControlData[] = documentData.map((item) => {
      return {
        type: StepControlType.ManualInput,
        identifier: uuidv4(),
        title: item,
        specificControlData: {
          type: ManualInputControlType.Text,
        },
      } as ControlData;
    });

    const documentSection: SectionData = {
      title: this.translate.transform(LabelKeys.flux.stepInfo.kycPersonTitle),
      type: SectionType.Presentation,
      controls: documentControls,
      identifier: this.generateUniqueId(
        this.translate.transform(LabelKeys.flux.stepInfo.kycPersonTitle)
      ),
    } as SectionData;

    // Section 3: Validation Data
    const validationSection: SectionData = {
      identifier: this.generateUniqueId(
        this.translate.transform(LabelKeys.flux.stepInfo.kycValidationTitle)
      ),
      title: this.translate.transform(
        LabelKeys.flux.stepInfo.kycValidationTitle
      ),
      controls: [
        {
          canEdit: true,
          canDepend: false,
          isOnlyForConfig: false,
          type: StepControlType.ManualInput,
          identifier: 'ClientPhoneNumber',
          title: 'ClientPhoneNumber',
          specificControlData: {
            type: ManualInputControlType.Phone,
          },
        } as ControlData,
        {
          canEdit: true,
          canDepend: false,
          isOnlyForConfig: false,
          type: StepControlType.ManualInput,
          identifier: 'IdSnapshot',
          title: 'IdSnapshot',
          specificControlData: {
            type: ManualInputControlType.Text,
          },
        } as ControlData,
        {
          canEdit: true,
          canDepend: false,
          isOnlyForConfig: false,
          type: StepControlType.ManualInput,
          identifier: 'FaceSnapshot',
          title: 'FaceSnapshot',
          specificControlData: {
            type: ManualInputControlType.Text,
          },
        } as ControlData,
      ],
      canDepend: true,
    } as SectionData;

    // Section 4: Specific Document Data (Address)
    const specificDocumentData = [
      'AddressStreetType',
      'AddressStreet',
      'AddressBlockNumber',
      'AddressEntrance',
      'AddressFloorNumber',
      'AddressFlat',
      'AddressCityType',
      'AddressCity',
      'AddressCountyType',
      'AddressArea',
      'Address',
    ];
    const specificDocumentControls: ControlData[] = specificDocumentData.map((item) => {
      return {
        type: StepControlType.ManualInput,
        identifier: uuidv4(),
        title: item,
        specificControlData: {
          type: ManualInputControlType.Text,
        },
      } as ControlData;
    });

    const addressSection: SectionData = {
      identifier: this.generateUniqueId(
        this.translate.transform(LabelKeys.flux.stepInfo.kycAddressTitle)
      ),
      title: this.translate.transform(LabelKeys.flux.stepInfo.kycAddressTitle),
      type: SectionType.Presentation,
      controls: specificDocumentControls,
      canDepend: true,
    } as SectionData;

    // Section 5: Risk Indicators
    const riskIndicatorsData = [
      'AgeRangeFromSelfieImageLow',
      'AgeRangeFromSelfieImageHigh',
      'GenderFromSelfieImage',
      'AgeAtDocumentIssue',
      'AgeEstimationFromDocumentImageLow',
      'AgeEstimationFromDocumentImageHigh',
      'CurrentAge',
    ];
    const riskIndicatorsControls: ControlData[] = riskIndicatorsData.map((item) => {
      return {
        type: StepControlType.ManualInput,
        identifier: uuidv4(),
        title: item,
        specificControlData: {
          type: ManualInputControlType.Text,
        },
      } as ControlData;
    });

    const riskFlagsData = [
      'MultipleFaces',
      'SameFaceDifferentUniqueNumber',
      'PhoneUsedMultipleTimes',
      'DifferentGender',
      'AgeMissmatch',
      'SuspiciousFace',
    ];
    const riskFlagsControls: ControlData[] = riskFlagsData.map((item) => {
      return {
        type: StepControlType.CheckBox,
        identifier: uuidv4(),
        title: item,
        specificControlData: {},
      } as ControlData;
    });

    const riskIndicatorsSection: SectionData = {
      identifier: this.generateUniqueId('Risk Indicators'),
      title: 'Risk Indicators',
      type: SectionType.Presentation,
      controls: [...riskIndicatorsControls, ...riskFlagsControls],
      canDepend: true,
    } as SectionData;

    // Section 6: ROeID Data
    const roeIdData = [
      'ROeID_Surname',
      'ROeID_GivenNames',
      'ROeID_Sex',
      'ROeID_CNP',
      'ROeID_Country',
      'ROeID_DateOfBirth',
      'ROeID_PlaceOfBirth',
      'ROeID_IssuerCode',
      'ROeID_IssueDate',
      'ROeID_ExpiryDate',
      'ROeID_IssuerName',
      'ROeID_AddressLine',
      'ROeID_City',
      'ROeID_Sector',
      'ROeID_TempAddressValidFrom',
      'ROeID_TempAddressValidTo',
      'ROeID_ForeignAddressValidFrom',
      'ROeID_ForeignAddressValidTo',
    ];
    const roeIdControls: ControlData[] = roeIdData.map((item) => {
      return {
        type: StepControlType.ManualInput,
        identifier: uuidv4(),
        title: item,
        specificControlData: {
          type: ManualInputControlType.Text,
        },
      } as ControlData;
    });

    const roeIdSection: SectionData = {
      identifier: this.generateUniqueId('ROeID Data'),
      title: 'ROeID Data',
      type: SectionType.Presentation,
      controls: roeIdControls,
      canDepend: true,
    } as SectionData;

    step.sections = [
      statusSection,
      documentSection,
      validationSection,
      addressSection,
      riskIndicatorsSection,
      roeIdSection,
    ];
    return step;
  }

  /**
   * Create Email Verification step
   */
  createCheckEmailStep(supportedLanguages: Language[]): StepData {
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: this.translate.transform(LabelKeys.flux.verifyEmail),
      type: FluxType.Email,
      sections: [],
      isVisible: true,
      config: {
        collected: false,
        componentCollected: '',
      },
      translations:
        TranslationsHelper.createCheckEmailTranslations(supportedLanguages),
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    // Add email verification section
    const section: SectionData = {
      identifier: `Sistem - Email_${this.generateUniqueId('sec')}`,
      title: 'Sistem - Email',
      type: SectionType.Presentation,
      controls: [
        {
          canEdit: true,
          canDepend: false,
          isOnlyForConfig: false,
          type: StepControlType.ManualInput,
          identifier: 'validated-email',
          title: 'Email Valid',
          specificControlData: {
            type: ManualInputControlType.Email,
          },
        } as ControlData,
      ],
      canDepend: true,
    } as SectionData;

    step.sections = [section];
    return step;
  }

  /**
   * Create Phone Verification step
   */
  createCheckPhoneStep(supportedLanguages: Language[]): StepData {
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: this.translate.transform(LabelKeys.flux.verifyPhone),
      type: FluxType.Phone,
      sections: [],
      isVisible: true,
      config: {
        collected: false,
        componentCollected: '',
      },
      translations:
        TranslationsHelper.createCheckPhoneTranslations(supportedLanguages),
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    // Add phone verification section
    const section: SectionData = {
      identifier: `Sistem - Tel_${this.generateUniqueId('sec')}`,
      title: 'Sistem - Tel',
      type: SectionType.Presentation,
      controls: [
        {
          canEdit: true,
          canDepend: false,
          isOnlyForConfig: false,
          type: StepControlType.ManualInput,
          identifier: 'validated-phone',
          title: 'Nr tel Valid',
          specificControlData: {
            type: ManualInputControlType.Phone,
          },
        } as ControlData,
      ],
      canDepend: true,
    } as SectionData;

    step.sections = [section];
    return step;
  }

  /**
   * Create ID Scan step
   */
  createScanStep(supportedLanguages: Language[]): StepData {
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: this.translate.transform(LabelKeys.flux.idScan),
      type: FluxType.IdScan,
      sections: [],
      isVisible: true,
      config: {
        showUploadFileButton: true,
        showTakePictureButton: true,
        fileMaxSize: 5000,
        fileFormats: '*.jpg,*.png',
      },
      translations:
        TranslationsHelper.createIdScanTranslations(supportedLanguages),
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      errorsTranslations: [
        {
          code: 'invalidStatus',
          translations: this.createTranslationsIfSupported(supportedLanguages, [
            { iso: 'en', value: 'Invalid status response' },
            { iso: 'ro', value: 'Raspuns status invalid' },
          ]),
        },
        {
          code: 'invalidType',
          translations: this.createTranslationsIfSupported(supportedLanguages, [
            { iso: 'en', value: 'Invalid type. Allowed formats are {formats}' },
            { iso: 'ro', value: 'Tip invalid. Formatul permis este {formats}' },
          ]),
        },
        {
          code: 'maxSize',
          translations: this.createTranslationsIfSupported(supportedLanguages, [
            {
              iso: 'en',
              value: 'The selected file is too big. Max allowed size is {size}',
            },
            {
              iso: 'ro',
              value:
                'Fișierul selectat este prea mare. Dimensiunea maximă este {size}',
            },
          ]),
        },
        {
          code: 'frontId',
          translations: this.createTranslationsIfSupported(supportedLanguages, [
            {
              iso: 'en',
              value: 'Please upload the front photo of your ID card.',
            },
            {
              iso: 'ro',
              value:
                'Vă rugăm să urcați partea din față a documentului de identitate.',
            },
          ]),
        },
        {
          code: 'backId',
          translations: this.createTranslationsIfSupported(supportedLanguages, [
            {
              iso: 'en',
              value: 'Please upload the back photo of your ID card.',
            },
            {
              iso: 'ro',
              value:
                'Vă rugăm să urcați partea din spate a documentului de identitate.',
            },
          ]),
        },
      ],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    // Match V1 ID Scan sections exactly
    // Status section
    const statusSection: SectionData = {
      identifier: this.generateUniqueId('ID SCAN Status'),
      title: 'ID SCAN Status',
      type: SectionType.Presentation,
      controls: [
        {
          type: StepControlType.CheckBox,
          identifier: this.generateUniqueId('ID SCAN Validation Status'),
          title: 'ID SCAN Validation Status',
        } as ControlData,
      ],
    } as SectionData;

    // Person data section
    const personData = [
      'CNP',
      'IDSeries',
      'IDNumber',
      'FirstName',
      'LastName',
      'Nationality',
      'IDExpirationDate',
      'IDValidity',
      'Address',
      'PlaceOfBirth',
    ];
    const personControls: ControlData[] = personData.map((item) => {
      return {
        type: StepControlType.ManualInput,
        identifier: this.generateUniqueId(item),
        title: item,
        specificControlData: {
          type: ManualInputControlType.Text,
        },
      } as ControlData;
    });

    const personSection: SectionData = {
      identifier: this.generateUniqueId('ID SCAN Persoana'),
      title: 'ID SCAN Persoana',
      type: SectionType.Presentation,
      controls: personControls,
    } as SectionData;

    step.sections = [statusSection, personSection];
    return step;
  }

  /**
   * Create Sign step
   */
  createSignStep(supportedLanguages: Language[]): StepData {
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: this.translate.transform(LabelKeys.flux.sign),
      type: FluxType.Sign,
      sections: [],
      isVisible: true,
      config: {
        signatureType: 'Electronic',
        showConfirm: true,
        companySign: false,
        firstNameCollected: '',
        lastNameCollected: '',
        fileNameTemplate: '',
        documents: [],
        docsToSign: [],
      },
      translations:
        TranslationsHelper.createSignTranslations(supportedLanguages),
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    // Add sign section
    const section: SectionData = {
      identifier: this.generateUniqueId('sec'),
      title: 'Document Signing',
      type: SectionType.Presentation,
      controls: [
        this.createParagraphControl(
          'Please sign the following documents',
          'sign-instruction',
          false,
          false
        ),
        {
          identifier: this.generateUniqueId('comp'),
          type: StepControlType.ViewDocuments,
          title: 'Documents to Sign',
          canEdit: false,
          canDepend: false,
          isOnlyForConfig: false,
          dimension: 100,
          dimensionType: DimensionType.Percentage,
          autoDimension: true,
          isPresentationControl: false,
          specificControlData: {
            documents: [],
          },
          errorsTranslations: [],
          dependentDictionary: {
            isDictionaryDependent: false,
            dictionaryControlIdentifier: '',
            dependentValueIds: [],
          },
          isMandatory: false,
          translations: [],
        },
      ],
      presentationControls: [],
      isNavigationSection: false,
      canDepend: false,
      translations: [],
      collapsed: false,
      showSectionTitle: true,
      showSectionSeparator: true,
    };

    step.sections = [section];
    return step;
  }

  /**
   * Create Generate Documents step
   */
  createGenerateDocumentsStep(supportedLanguages: Language[]): StepData {
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: this.translate.transform(LabelKeys.flux.generateDocument),
      type: FluxType.GenerateDocument,
      sections: [],
      isVisible: true,
      config: {
        generatedDocuments: [],
      },
      translations:
        TranslationsHelper.createGenerateDocumentsTranslations(
          supportedLanguages
        ),
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    // No default sections for generate documents
    return step;
  }

  /**
   * Create Pause step
   */
  createPauseStep(supportedLanguages: Language[]): StepData {
    const stepName = this.translate.transform(LabelKeys.flux.pause);
    const step: StepData = {
      identifier: this.generateUniqueId(`${stepName} - Pause`),
      name: stepName,
      type: FluxType.Pause,
      sections: [],
      isVisible: true,
      config: {},
      translations:
        TranslationsHelper.createPauseTranslations(supportedLanguages),
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    // Add pause message section
    const section: SectionData = {
      identifier: this.generateUniqueId('sec'),
      title: 'Flow Paused',
      type: SectionType.Presentation,
      controls: [
        this.createParagraphControl(
          'The workflow has been paused',
          'pause-message',
          false,
          false
        ),
      ],
      presentationControls: [],
      isNavigationSection: false,
      canDepend: false,
      translations: [],
      collapsed: false,
      showSectionTitle: true,
      showSectionSeparator: true,
    };

    step.sections = [section];
    return step;
  }

  /**
   * Create Action step
   */
  createActionStep(supportedLanguages: Language[]): StepData {
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: this.translate.transform(LabelKeys.flux.action),
      type: FluxType.Action,
      sections: [],
      isVisible: true,
      config: {
        apiMethod: '',
        apiPartners: [],
      },
      translations:
        TranslationsHelper.createActionTranslations(supportedLanguages),
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    // No default sections for action step
    return step;
  }

  createValidationStep(supportedLanguages: Language[]): StepData {
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: this.translate.transform(LabelKeys.flux.validation),
      type: FluxType.Validation,
      sections: [],
      isVisible: true,
      config: {
        comparisons: [],
        dictionaryUUID: '',
        trueValueId: null,
        falseValueId: null,
      },
      translations: TranslationsHelper.createValidationTranslations(supportedLanguages),
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    return step;
  }

  /**
   * Create Status step with default section
   */
  createStatusStep(supportedLanguages: Language[]): StepData {
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: this.translate.transform(LabelKeys.flux.status),
      type: FluxType.Status,
      sections: [],
      isVisible: true,
      config: {},
      panelOpenState: true,
      buttons: [],
      translations:
        TranslationsHelper.createStatusTranslations(supportedLanguages),
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    // Add status selection section
    const section: SectionData = {
      canDepend: true,
      title: 'Status Sistem',
      type: SectionType.Presentation,
      controls: [
        {
          canEdit: true,
          canDepend: false,
          isOnlyForConfig: false,
          type: StepControlType.NewRow,
          identifier: 'select-status',
          title: 'Flux',
          specificControlData: {
            type: ManualInputControlType.Text,
          },
        } as ControlData,
      ],
      identifier: `Status Sistem_${this.generateUniqueId('sec')}`,
    } as SectionData;

    step.sections = [section];
    return step;
  }

  /**
   * Create Upload Document step
   */
  createUploadDocumentStep(supportedLanguages: Language[]): StepData {
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: this.translate.transform(LabelKeys.flux.uploadDocument),
      type: FluxType.UploadDocument,
      sections: [],
      isVisible: true,
      config: {},
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      translations: [],
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    // Add upload section
    const section: SectionData = {
      identifier: this.generateUniqueId('sec'),
      title: 'Upload Documents',
      type: SectionType.Collect,
      controls: [
        {
          identifier: this.generateUniqueId('comp'),
          type: StepControlType.UploadDocuments,
          title: this.translate.transform(LabelKeys.flux.uploadDocument),
          canEdit: false,
          canDepend: false,
          isOnlyForConfig: false,
          isPresentationControl: false,
          dimension: 100,
          dimensionType: DimensionType.Percentage,
          autoDimension: true,
          specificControlData: {
            documents: [],
            fileMaxSize: 10000,
            fileFormats: '*.pdf,*.jpg,*.png,*.doc,*.docx',
            isMandatory: true,
          },
          errorsTranslations: [],
          dependentDictionary: {
            isDictionaryDependent: false,
            dictionaryControlIdentifier: '',
            dependentValueIds: [],
          },
          isMandatory: false,
          translations: [],
        },
      ],
      presentationControls: [],
      isNavigationSection: false,
      canDepend: false,
      translations: [],
      collapsed: false,
      showSectionTitle: true,
      showSectionSeparator: true,
    };

    step.sections = [section];
    return step;
  }

  /**
   * Create Download Document step
   */
  createDownloadDocumentStep(supportedLanguages: Language[]): StepData {
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: this.translate.transform(LabelKeys.flux.downloadDocument),
      type: FluxType.DownloadDocument,
      sections: [],
      isVisible: true,
      config: {},
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      translations: [],
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    // Add download section
    const section: SectionData = {
      identifier: this.generateUniqueId('sec'),
      title: 'Download Documents',
      type: SectionType.Presentation,
      controls: [
        {
          identifier: this.generateUniqueId('comp'),
          type: StepControlType.DownloadDocuments,
          title: this.translate.transform(LabelKeys.flux.downloadDocument),
          canEdit: false,
          canDepend: false,
          isOnlyForConfig: false,
          isPresentationControl: false,
          dimension: 100,
          dimensionType: DimensionType.Percentage,
          autoDimension: true,
          specificControlData: {
            documents: [],
          },
          errorsTranslations: [],
          dependentDictionary: {
            isDictionaryDependent: false,
            dictionaryControlIdentifier: '',
            dependentValueIds: [],
          },
          isMandatory: false,
          translations: [],
        },
      ],
      presentationControls: [],
      isNavigationSection: false,
      canDepend: false,
      translations: [],
      collapsed: false,
      showSectionTitle: true,
      showSectionSeparator: true,
    };

    step.sections = [section];
    return step;
  }

  /**
   * Create View Document step
   */
  createViewDocumentStep(supportedLanguages: Language[]): StepData {
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: this.translate.transform(LabelKeys.flux.viewDocument),
      type: FluxType.ViewDocument,
      sections: [],
      isVisible: true,
      config: {},
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      translations: [],
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    // Add view section
    const section: SectionData = {
      identifier: this.generateUniqueId('sec'),
      title: 'View Documents',
      type: SectionType.Presentation,
      controls: [
        {
          identifier: this.generateUniqueId('comp'),
          type: StepControlType.ViewDocuments,
          title: this.translate.transform(LabelKeys.flux.viewDocument),
          canEdit: false,
          canDepend: false,
          isOnlyForConfig: false,
          isPresentationControl: false,
          dimension: 100,
          dimensionType: DimensionType.Percentage,
          autoDimension: true,
          specificControlData: {
            documents: [],
          },
          errorsTranslations: [],
          dependentDictionary: {
            isDictionaryDependent: false,
            dictionaryControlIdentifier: '',
            dependentValueIds: [],
          },
          isMandatory: false,
          translations: [],
        },
      ],
      presentationControls: [],
      isNavigationSection: false,
      canDepend: false,
      translations: [],
      collapsed: false,
      showSectionTitle: true,
      showSectionSeparator: true,
    };

    step.sections = [section];
    return step;
  }

  /**
   * Create View Offer step
   */
  createViewOfferStep(supportedLanguages: Language[]): StepData {
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: this.translate.transform(LabelKeys.flux.viewOffer),
      type: FluxType.ViewOffer,
      sections: [],
      isVisible: true,
      config: {},
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      translations:
        TranslationsHelper.createViewOfferStepTranslations(supportedLanguages),
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    // No default sections for view offer
    return step;
  }

  /**
   * Create NETOPIA Payment step
   */
  createNetopiaStep(supportedLanguages: Language[]): StepData {
    const stepName = this.translate.transform(LabelKeys.flux.netopia);
    const step: StepData = {
      identifier: this.generateUniqueId(`${stepName} - NETOPIA`),
      name: stepName,
      type: FluxType.NETOPIA,
      sections: [],
      isVisible: true,
      config: {},
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      translations:
        TranslationsHelper.createPaymentsTranslations(supportedLanguages),
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    const section: SectionData = {
      identifier: this.generateUniqueId('sec'),
      title: 'Netopia Payment',
      type: SectionType.Presentation,
      controls: [],
      presentationControls: [],
      isNavigationSection: false,
      canDepend: false,
      translations: [],
      collapsed: false,
      showSectionTitle: true,
      showSectionSeparator: true,
    };
    step.sections = [section];

    // No default sections for payment steps
    return step;
  }

  /**
   * Create EUPLATESC Payment step
   */
  createEuplatescStep(supportedLanguages: Language[]): StepData {
    const stepName = this.translate.transform(LabelKeys.flux.euPlatesc);
    const step: StepData = {
      identifier: this.generateUniqueId(`${stepName} - EUPLATESC`),
      name: stepName,
      type: FluxType.EUPLATESC,
      sections: [],
      isVisible: true,
      config: {},
      translations:
        TranslationsHelper.createPaymentsTranslations(supportedLanguages),
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    const section: SectionData = {
      identifier: this.generateUniqueId('sec'),
      title: 'EuPlatesc Payment',
      type: SectionType.Presentation,
      controls: [],
      presentationControls: [],
      isNavigationSection: false,
      canDepend: false,
      translations: [],
      collapsed: false,
      showSectionTitle: true,
      showSectionSeparator: true,
    };
    step.sections = [section];

    // No default sections for payment steps
    return step;
  }

  /**
   * Create Open Banking step with default sections and controls
   */
  createOpenBankingStep(supportedLanguages: Language[]): StepData {
    const stepName = this.translate.transform(LabelKeys.flux.openBanking);
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: stepName,
      type: FluxType.OpenBanking,
      sections: [],
      isVisible: true,
      config: {
        // Field mappings
        accountNumberCollected: '',
        accountHolderCollected: '',
        emailCollected: '',
        psuIntermediaryId: '',

        // Bank settings
        showAllBanks: true,
        extractBankFromIBAN: false,
        countryCode: 'RO',

        // Transaction settings
        transactionPeriodMonths: 3,
        periodOfValidity: 90,
        tcAcceptedByDefault: true,
        redirectUrl: '',

        // Messages - will be initialized with translations
        initialMessageTranslations: this.createTranslationsIfSupported(
          supportedLanguages,
          [
            {
              iso: 'en',
              value:
                'To continue, we need to verify your bank account information. Click Next to proceed.',
            },
            {
              iso: 'ro',
              value:
                'Pentru a continua, trebuie să verificăm informațiile contului dvs. bancar. Faceți clic pe Următorul pentru a continua.',
            },
          ]
        ),
        waitingMessageTranslations: this.createTranslationsIfSupported(
          supportedLanguages,
          [
            {
              iso: 'en',
              value:
                'Please wait while we retrieve your account information...',
            },
            {
              iso: 'ro',
              value:
                'Vă rugăm să așteptați până când preluăm informațiile contului dvs...',
            },
          ]
        ),
        consentErrorMessageTranslations: this.createTranslationsIfSupported(
          supportedLanguages,
          [
            {
              iso: 'en',
              value: 'You must provide consent to continue. Please try again.',
            },
            {
              iso: 'ro',
              value:
                'Trebuie să oferiți consimțământul pentru a continua. Vă rugăm să încercați din nou.',
            },
          ]
        ),
        completionMessageTranslations: this.createTranslationsIfSupported(
          supportedLanguages,
          [
            { iso: 'en', value: 'Account information successfully retrieved.' },
            {
              iso: 'ro',
              value: 'Informațiile contului au fost preluate cu succes.',
            },
          ]
        ),
        retryButtonLabelTranslations: this.createTranslationsIfSupported(
          supportedLanguages,
          [
            { iso: 'en', value: 'Retry' },
            { iso: 'ro', value: 'Reîncearcă' },
          ]
        ),
      },
      translations:
        TranslationsHelper.createOpenBankingTranslations(supportedLanguages),
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    // Section 1: Consent Status
    const statusSection: SectionData = {
      title: 'Consent Status',
      type: SectionType.Presentation,
      controls: [
        {
          type: StepControlType.CheckBox,
          identifier: 'OpenBankingConsentStatus',
          title: 'Consent Validated',
          specificControlData: {},
        } as ControlData,
      ],
      identifier: this.generateUniqueId('Consent Status'),
    } as SectionData;

    // Section 2: Account Information
    const accountControls: ControlData[] = [
      {
        type: StepControlType.ManualInput,
        identifier: 'OpenBankingEmail',
        title: 'Email',
        specificControlData: {
          type: ManualInputControlType.Email,
        },
      },
      {
        type: StepControlType.ManualInput,
        identifier: 'OpenBankingAccountNumber',
        title: 'Account Number',
        specificControlData: {
          type: ManualInputControlType.Text,
        },
      },
      {
        type: StepControlType.ManualInput,
        identifier: 'OpenBankingAccountHolder',
        title: 'Account Holder Name',
        specificControlData: {
          type: ManualInputControlType.Text,
        },
      },
      {
        type: StepControlType.ManualInput,
        identifier: 'OpenBankingBankCode',
        title: 'Bank Code',
        specificControlData: {
          type: ManualInputControlType.Text,
        },
      },
      {
        type: StepControlType.ManualInput,
        identifier: 'OpenBankingConsentId',
        title: 'Consent ID',
        specificControlData: {
          type: ManualInputControlType.Text,
        },
      },
    ] as ControlData[];

    const accountSection: SectionData = {
      title: 'Account Information',
      type: SectionType.Presentation,
      controls: accountControls,
      identifier: this.generateUniqueId('Account Information'),
    } as SectionData;

    // Section 3: Accounts Retrieved
    const accountsDataControls: ControlData[] = [
      {
        type: StepControlType.ManualInput,
        identifier: 'OpenBankingAccountsCount',
        title: 'Number of Accounts',
        specificControlData: {
          type: ManualInputControlType.Numeric,
        },
      },
      {
        type: StepControlType.ManualInput,
        identifier: 'OpenBankingAccountsData',
        title: 'Accounts Data (JSON)',
        specificControlData: {
          type: ManualInputControlType.TextArea,
        },
      },
      {
        type: StepControlType.ManualInput,
        identifier: 'OpenBankingTotalBalance',
        title: 'Total Balance',
        specificControlData: {
          type: ManualInputControlType.Numeric,
        },
      },
    ] as ControlData[];

    const accountsDataSection: SectionData = {
      title: 'Retrieved Accounts',
      type: SectionType.Presentation,
      controls: accountsDataControls,
      identifier: this.generateUniqueId('Retrieved Accounts'),
    } as SectionData;

    // Section 4: Transactions Retrieved
    const transactionsControls: ControlData[] = [
      {
        type: StepControlType.ManualInput,
        identifier: 'OpenBankingTransactionsCount',
        title: 'Number of Transactions',
        specificControlData: {
          type: ManualInputControlType.Numeric,
        },
      },
      {
        type: StepControlType.ManualInput,
        identifier: 'OpenBankingTransactionsData',
        title: 'Transactions Data (JSON)',
        specificControlData: {
          type: ManualInputControlType.TextArea,
        },
      },
      {
        type: StepControlType.ManualInput,
        identifier: 'OpenBankingTransactionsPeriod',
        title: 'Period (months)',
        specificControlData: {
          type: ManualInputControlType.Numeric,
        },
      },
    ] as ControlData[];

    const transactionsSection: SectionData = {
      title: 'Retrieved Transactions',
      type: SectionType.Presentation,
      controls: transactionsControls,
      identifier: this.generateUniqueId('Retrieved Transactions'),
    } as SectionData;

    // Add all sections to the step
    step.sections = [
      statusSection,
      accountSection,
      accountsDataSection,
      transactionsSection,
    ];

    return step;
  }

  /**
   * Create OmniCheck Person step
   */
  createOmniCheckPersonStep(supportedLanguages: Language[]): StepData {
    const stepName = this.translate.transform(LabelKeys.flux.omniCheckPerson);
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: stepName,
      type: FluxType.OmniCheckPerson,
      sections: [],
      isVisible: true,
      config: {
        firstNameCollected: '',
        lastNameCollected: '',
        dateOfBirthCollected: '',
        cnpCollected: '',
        reportDocumentId: '',
        threshold: 80,
        waitingMessageTranslations: this.createTranslationsIfSupported(
          supportedLanguages,
          [
            { iso: 'en', value: 'Please wait while we verify your identity...' },
            { iso: 'ro', value: 'Va rugam asteptati cat timp verificam identitatea dumneavoastra...' },
          ]
        ),
      },
      translations:
        TranslationsHelper.createOmniCheckPersonTranslations(supportedLanguages),
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    const resultsSection: SectionData = {
      title: 'OmniCheck Results',
      type: SectionType.Presentation,
      controls: [
        {
          type: StepControlType.ManualInput,
          identifier: 'OmniCheckPersonResultsData',
          title: 'Results',
          specificControlData: { type: ManualInputControlType.TextArea },
        } as ControlData,
        {
          type: StepControlType.ManualInput,
          identifier: 'OmniCheckPersonMaxScore',
          title: 'Max Score',
          specificControlData: { type: ManualInputControlType.Numeric },
        } as ControlData,
        {
          type: StepControlType.CheckBox,
          identifier: 'OmniCheckPersonPepCurrent',
          title: 'Potential PEP Current',
          specificControlData: {},
        } as ControlData,
        {
          type: StepControlType.CheckBox,
          identifier: 'OmniCheckPersonPepFormer',
          title: 'Potential PEP Former',
          specificControlData: {},
        } as ControlData,
        {
          type: StepControlType.CheckBox,
          identifier: 'OmniCheckPersonPepLinked',
          title: 'Potential PEP Linked',
          specificControlData: {},
        } as ControlData,
        {
          type: StepControlType.CheckBox,
          identifier: 'OmniCheckPersonSanCurrent',
          title: 'Potential SAN Current',
          specificControlData: {},
        } as ControlData,
        {
          type: StepControlType.CheckBox,
          identifier: 'OmniCheckPersonSanFormer',
          title: 'Potential SAN Former',
          specificControlData: {},
        } as ControlData,
        {
          type: StepControlType.CheckBox,
          identifier: 'OmniCheckPersonRre',
          title: 'Potential RRE',
          specificControlData: {},
        } as ControlData,
        {
          type: StepControlType.CheckBox,
          identifier: 'OmniCheckPersonPoi',
          title: 'Potential POI',
          specificControlData: {},
        } as ControlData,
        {
          type: StepControlType.CheckBox,
          identifier: 'OmniCheckPersonRel',
          title: 'Potential REL',
          specificControlData: {},
        } as ControlData,
      ],
      identifier: this.generateUniqueId('OmniCheck Results'),
    } as SectionData;

    step.sections = [resultsSection];
    return step;
  }

  /**
   * Create OmniCheck Business step
   */
  createOmniCheckBusinessStep(supportedLanguages: Language[]): StepData {
    const stepName = this.translate.transform(LabelKeys.flux.omniCheckBusiness);
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: stepName,
      type: FluxType.OmniCheckBusiness,
      sections: [],
      isVisible: true,
      config: {
        companyNameCollected: '',
        cuiCollected: '',
        reportDocumentId: '',
        threshold: 80,
        waitingMessageTranslations: this.createTranslationsIfSupported(
          supportedLanguages,
          [
            { iso: 'en', value: 'Please wait while we verify your company...' },
            { iso: 'ro', value: 'Va rugam asteptati cat timp verificam compania dumneavoastra...' },
          ]
        ),
      },
      translations:
        TranslationsHelper.createOmniCheckBusinessTranslations(supportedLanguages),
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    const resultsSection: SectionData = {
      title: 'OmniCheck Results',
      type: SectionType.Presentation,
      controls: [
        {
          type: StepControlType.ManualInput,
          identifier: 'OmniCheckBusinessResultsData',
          title: 'Results',
          specificControlData: { type: ManualInputControlType.TextArea },
        } as ControlData,
        {
          type: StepControlType.ManualInput,
          identifier: 'OmniCheckBusinessMaxScore',
          title: 'Max Score',
          specificControlData: { type: ManualInputControlType.Numeric },
        } as ControlData,
        {
          type: StepControlType.CheckBox,
          identifier: 'OmniCheckBusinessPepCurrent',
          title: 'Potential PEP Current',
          specificControlData: {},
        } as ControlData,
        {
          type: StepControlType.CheckBox,
          identifier: 'OmniCheckBusinessPepFormer',
          title: 'Potential PEP Former',
          specificControlData: {},
        } as ControlData,
        {
          type: StepControlType.CheckBox,
          identifier: 'OmniCheckBusinessPepLinked',
          title: 'Potential PEP Linked',
          specificControlData: {},
        } as ControlData,
        {
          type: StepControlType.CheckBox,
          identifier: 'OmniCheckBusinessSanCurrent',
          title: 'Potential SAN Current',
          specificControlData: {},
        } as ControlData,
        {
          type: StepControlType.CheckBox,
          identifier: 'OmniCheckBusinessSanFormer',
          title: 'Potential SAN Former',
          specificControlData: {},
        } as ControlData,
        {
          type: StepControlType.CheckBox,
          identifier: 'OmniCheckBusinessRre',
          title: 'Potential RRE',
          specificControlData: {},
        } as ControlData,
        {
          type: StepControlType.CheckBox,
          identifier: 'OmniCheckBusinessPoi',
          title: 'Potential POI',
          specificControlData: {},
        } as ControlData,
        {
          type: StepControlType.CheckBox,
          identifier: 'OmniCheckBusinessRel',
          title: 'Potential REL',
          specificControlData: {},
        } as ControlData,
      ],
      identifier: this.generateUniqueId('OmniCheck Results'),
    } as SectionData;

    step.sections = [resultsSection];
    return step;
  }

  /**
   * Create Custom step with navigation section
   */
  createCustomStep(supportedLanguages: Language[]): StepData {
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: this.translate.transform(LabelKeys.flux.customStep),
      type: FluxType.Custom,
      sections: [],
      isVisible: true,
      config: {},
      translations:
        TranslationsHelper.createCustomStepTranslations(supportedLanguages),
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    // Add navigation section
    const navigationSection: SectionData = {
      canDepend: true,
      title: 'Secțiune de Navigare',
      type: SectionType.Presentation,
      controls: [
        {
          canEdit: true,
          canDepend: false,
          isOnlyForConfig: false,
          type: StepControlType.Buttons,
        } as ControlData,
      ],
      isNavigationSection: true,
      identifier: `Secțiune de Navigare_${this.generateUniqueId('sec')}`,
    } as SectionData;

    step.sections = [navigationSection];
    return step;
  }

  /**
   * Create Final step
   */
  createFinalStep(supportedLanguages: Language[]): StepData {
    const step: StepData = {
      identifier: this.generateUniqueId('step'),
      name: this.translate.transform(LabelKeys.flux.finalStep),
      type: FluxType.Final,
      sections: [],
      isVisible: true,
      config: {},
      translations: [],
      panelOpenState: true,
      buttons: [],
      dependentDictionary: {
        isDictionaryDependent: false,
        dictionaryControlIdentifier: '',
        dependentValueIds: [],
      },
      errorsTranslations: [],
      hasNameAndDescription: true,
      canDepend: true,
      collapsed: false,
    };

    // Final step is like custom but without navigation
    return step;
  }

  /**
   * Main factory method to create any step type
   */
  createFluxStep(
    type: FluxType,
    supportedLanguages: Language[]
  ): StepData | null {
    switch (type) {
      case FluxType.Kyc:
        return this.createKycStep(supportedLanguages);
      case FluxType.Email:
        return this.createCheckEmailStep(supportedLanguages);
      case FluxType.Phone:
        return this.createCheckPhoneStep(supportedLanguages);
      case FluxType.Sign:
        return this.createSignStep(supportedLanguages);
      case FluxType.Status:
        return this.createStatusStep(supportedLanguages);
      case FluxType.Custom:
        return this.createCustomStep(supportedLanguages);
      case FluxType.Pause:
        return this.createPauseStep(supportedLanguages);
      case FluxType.IdScan:
        return this.createScanStep(supportedLanguages);
      case FluxType.GenerateDocument:
        return this.createGenerateDocumentsStep(supportedLanguages);
      case FluxType.Action:
        return this.createActionStep(supportedLanguages);
      case FluxType.UploadDocument:
        return this.createUploadDocumentStep(supportedLanguages);
      case FluxType.DownloadDocument:
        return this.createDownloadDocumentStep(supportedLanguages);
      case FluxType.ViewDocument:
        return this.createViewDocumentStep(supportedLanguages);
      case FluxType.ViewOffer:
        return this.createViewOfferStep(supportedLanguages);
      case FluxType.Final:
        return this.createFinalStep(supportedLanguages);
      case FluxType.NETOPIA:
        return this.createNetopiaStep(supportedLanguages);
      case FluxType.EUPLATESC:
        return this.createEuplatescStep(supportedLanguages);
      case FluxType.OpenBanking:
        return this.createOpenBankingStep(supportedLanguages);
      case FluxType.Validation:
        return this.createValidationStep(supportedLanguages);
      case FluxType.OmniCheckPerson:
        return this.createOmniCheckPersonStep(supportedLanguages);
      case FluxType.OmniCheckBusiness:
        return this.createOmniCheckBusinessStep(supportedLanguages);
      default:
        console.error(`Unsupported FluxType: ${type}`);
        return null;
    }
  }

  /**
   * Get document name by ID from form model
   * Static method to match FluxHelper usage pattern in forms.model.ts
   * @param documentId - The document ID to search for
   * @param formModel - The form model containing documents array
   * @returns The document name or empty string if not found
   */
  static getDocumentNameById(documentId: string, formModel: FormModel): string {
    if (!formModel || !formModel.documents || !documentId) {
      return '';
    }

    const document = formModel.documents.find(
      (d: AppDocument) => d.id === documentId
    );
    return document?.name || '';
  }

  /**
   * Check if the flux contains a remote identification (Kyc) step
   */
  static hasKycStep(steps: StepData[]): boolean {
    return (steps || []).some((s) => s.type === FluxType.Kyc);
  }

  /**
   * Check if the flux contains a sign step configured with qualified signature
   */
  static hasQualifiedSignStep(steps: StepData[]): boolean {
    return (steps || []).some(
      (s) =>
        s.type === FluxType.Sign &&
        String(s.config?.signatureType) === String(SignatureType.Qualified)
    );
  }
}
